import { ChatConversation, OrganizationMember } from '@prisma/client'
import { useLocation, useNavigate } from '@remix-run/react'
import { Divider, Flex } from 'antd'
import React, { useMemo } from 'react'
import { OrganizationMemberFromContext } from '~/core/context/organization/hooks/useOrganizationMembers'
import { OrganizationMetricsOverview } from '~/features/organization/client/components/MetricsOverview'
import { OrganizationType } from '~/features/organization/common'
import { useSimpleLocalStorage } from '~/features/shared/hooks'
import { useChatContext } from '../../context/chat.context'
import { useEnsureConversation } from '../../hooks/useEnsureConversation'
import { ContactItem } from '../ContactItem'
import { useParticipantsTyping } from '../Thread/hooks/useParticipantsTyping'

type Props = {
  conversationId?: string
  organization: OrganizationType
  organizationMember: OrganizationMember
  organizationMembers?: OrganizationMemberFromContext[]
}

export const ContactList: React.FC<Props> = ({
  conversationId,
  organization,
  organizationMember,
  organizationMembers,
}) => {
  const navigate = useNavigate()
  const location = useLocation()

  const { conversations, findByOrganizationMember, refresh, countRefresh } =
    useChatContext()

  const ensureConversation = useEnsureConversation({
    organization,
    organizationMember,
  })

  const { typing } = useParticipantsTyping({
    conversationIds: conversations.map(conversation => conversation.id),
    organization,
    organizationMembers,
    onNewMessage: payload => {
      const canIgnore = conversationId === payload.conversationId

      if (canIgnore) {
        return
      }

      refresh()
    },
    onUnknownConversation: () => {
      refresh()
    },
  })

  const items = useMemo(() => {
    return organizationMembers
      .filter(candidate => candidate.id !== organizationMember.id)
      .map(member => {
        const conversation = findByOrganizationMember(member.id)
        const message = conversation?.messages?.[0]
        const participantMe = conversation?.participants?.find(
          item => item.organizationMemberId === organizationMember.id,
        )

        const key = conversation?.id ?? member?.id
        const isTyping = !!typing[conversation?.id]
        const countNotifications = participantMe?.countUnreadMessages ?? 0

        return {
          key,
          organizationMember: member,
          isTyping,
          countNotifications,
          message,
        }
      })
  }, [organizationMembers, conversations, typing, countRefresh])

  const handleSelect = async (key: string) => {
    let conversation: ChatConversation = conversations.find(
      candidate => candidate.id === key,
    )

    const member = organizationMembers.find(member => member.id === key)

    if (!conversation && member) {
      conversation = await ensureConversation.ensure(member)
    }

    if (conversation) {
      navigate(`/organizations/${organization.id}/chat/${conversation.id}`)
    }
  }

  useSimpleLocalStorage({
    isReady: conversations.length > 0,
    value: conversationId,
    valueDefault: conversations[0]?.id,
    keyLocalStorage: `${organization.id}-conversation`,
    onChange: conversationId => {
      // handleSelect(conversationId)
    },
  })

  return (
    <>
      <Flex vertical className="w-full">
        <OrganizationMetricsOverview organization={organization} />
        <ul
          className="w-full"
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          {items.map((item, index) => (
            <Flex key={item.key} vertical>
              {index > 0 && (
                <Divider className="m-2" style={{ opacity: 0.3 }} />
              )}
              <ContactItem
                organizationMember={item.organizationMember}
                organization={organization}
                message={item.message}
                isTyping={item.isTyping}
                countNotifications={item.countNotifications}
                isActive={conversationId === item.key}
                onClick={() => handleSelect(item.key)}
              />
            </Flex>
          ))}
        </ul>
      </Flex>
    </>
  )
}
